/**
 * Tiny, dependency-free Markdown → HTML renderer, for CS tool playbooks.
 *
 * Ported from the standalone CS Toolbox. Everything is HTML-escaped first and
 * only a known-safe subset of tags is emitted, which is what makes the output
 * safe to hand to dangerouslySetInnerHTML. Links and images are restricted to
 * http(s) so a `javascript:` URL in a playbook cannot become a script.
 *
 * Supported: # to #### headings, - * + and 1. lists, > blockquotes, ``` fenced
 * code, --- rules, **bold**, *italic*, `code`, [text](url) and ![alt](url).
 *
 * No Markdown library, for the same reason the original had none: this renders
 * text our own people wrote, the subset is small and stable, and a parser is a
 * dependency to keep patched.
 */

function escapeHtml(value: string): string {
  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Inline formatting. Takes raw text, escapes it, then adds the safe spans. */
function inline(text: string): string {
  let s = escapeHtml(text);
  /* Images before links, so the leading ! is not left stranded. */
  s = s.replace(
    /!\[([^\]]*)\]\((https?:\/\/[^\s")]+)\)/g,
    (_, alt: string, url: string) =>
      `<img src="${url}" alt="${alt.replace(/"/g, '')}" class="pb-img" loading="lazy">`,
  );
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s")]+)\)/g,
    (_, label: string, url: string) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  /* __bold__ and _italic_ only at word boundaries, so snake_case identifiers
     and URLs inside a playbook are left alone. */
  s = s.replace(/(^|[\s(])__(?=\S)([^_]+?)(?<=\S)__(?=[\s.,;:!?)]|$)/g, '$1<strong>$2</strong>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/(^|[\s(])_(?=\S)([^_]+?)(?<=\S)_(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  return s;
}

export function renderMarkdown(source: string | null | undefined): string {
  const lines = String(source ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
  const out: string[] = [];
  let paragraph: string[] = [];
  let i = 0;

  const flush = () => {
    if (paragraph.length) {
      out.push(`<p>${paragraph.map(inline).join('<br>')}</p>`);
      paragraph = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i]!;

    if (/^```/.test(line)) {
      flush();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i]!)) {
        code.push(lines[i]!);
        i++;
      }
      i++; // closing fence
      out.push(`<pre><code>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    if (/^\s*$/.test(line)) {
      flush();
      i++;
      continue;
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flush();
      out.push('<hr>');
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flush();
      const level = heading[1]!.length;
      out.push(`<h${level}>${inline(heading[2]!.trim())}</h${level}>`);
      i++;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flush();
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i]!)) {
        quote.push(lines[i]!.replace(/^\s*>\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${quote.map(inline).join('<br>')}</blockquote>`);
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i]!)) {
        items.push(`<li>${inline(lines[i]!.replace(/^\s*[-*+]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i]!)) {
        items.push(`<li>${inline(lines[i]!.replace(/^\s*\d+\.\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    paragraph.push(line);
    i++;
  }

  flush();
  return out.join('\n');
}

/**
 * The playbook as a standalone document, for "Open as a full page".
 *
 * Self-contained on purpose — it is opened as a blob in a new tab, so it cannot
 * reach the app's stylesheet and the tokens have to be inlined. They are the
 * Brand Direct values from styles/tokens.css, not the Cobalt palette this page
 * carried in the standalone app: navy structure, crimson accent, no green and
 * no amber. Dark mode follows the reader's system setting, because a blob URL
 * has no access to the in-app theme toggle.
 */
export function playbookPage(title: string, source: string | null | undefined): string {
  const body = renderMarkdown(source);
  const safeTitle = escapeHtml(title || 'Playbook');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${safeTitle} — Playbook</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800&family=Open+Sans:ital,wght@0,400;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
  /* Brand Direct — transcribed from web/src/styles/tokens.css. */
  :root {
    color-scheme: light dark;
    --paper:   #FFFFFF;
    --sunken:  #E3EBF2;
    --ink:     #14395C;
    --ink-2:   #2F4050;
    --muted:   #546778;
    --rule:    #DCE3EA;
    --accent:      #CE2029;
    --accent-text: #AE1920;
    --accent-wash: #FDEDED;
    --code-bg:  #14395C;
    --code-ink: #E3EBF2;
    --font-display: Montserrat, "Open Sans", Helvetica, Arial, sans-serif;
    --font-body: "Open Sans", system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
    --font-mono: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
  }
  /* Screen only: a printed playbook is a report like any other, and the
     light ramp above is the one that belongs on paper. */
  @media screen and (prefers-color-scheme: dark) {
    :root {
      --paper:  #06192A;
      --sunken: #041524;
      --ink:    #FFFFFF;
      --ink-2:  #C5D4E2;
      --muted:  #9CB3C9;
      --rule:   rgb(255 255 255 / 0.10);
      --accent:      #E98186;
      --accent-text: #E98186;
      --accent-wash: rgb(206 32 41 / 0.12);
      --code-bg:  #041524;
      --code-ink: #E3EBF2;
    }
  }
  * { box-sizing: border-box; }
  html, body { overflow-x: clip; }
  body { margin: 0; background: var(--paper); color: var(--ink-2);
    font-family: var(--font-body); font-size: 16px; line-height: 1.65;
    -webkit-font-smoothing: antialiased; }
  .wrap { max-width: 72ch; margin: 0 auto; padding: 56px 24px 96px; }
  h1.title { font-family: var(--font-display); font-weight: 700;
    font-size: clamp(1.75rem, 4vw + 0.5rem, 2.1875rem); letter-spacing: -0.02em;
    color: var(--ink); margin: 0 0 8px; line-height: 1.15; overflow-wrap: anywhere; }
  .meta { font-family: var(--font-mono); font-size: 11px; font-weight: 500;
    letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted);
    padding-bottom: 20px; border-bottom: 1px solid var(--rule); margin: 0 0 32px; }
  article h1, article h2, article h3 { font-family: var(--font-display);
    letter-spacing: -0.02em; color: var(--ink); overflow-wrap: anywhere; }
  article h1 { font-size: 22px; font-weight: 700; margin: 34px 0 12px; }
  article h2 { font-size: 18px; font-weight: 700; margin: 28px 0 10px; }
  article h3 { font-size: 16px; font-weight: 600; margin: 22px 0 8px; }
  article h4 { font-family: var(--font-mono); font-size: 11px; font-weight: 500; margin: 20px 0 6px;
    text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); }
  article p { margin: 0 0 14px; }
  article ul, article ol { margin: 0 0 14px; padding-left: 22px; }
  article li { margin: 5px 0; }
  article li::marker { color: var(--muted); }
  article a { color: var(--accent-text); font-weight: 600;
    text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 2px; }
  article a:hover { text-decoration-thickness: 2px; }
  article strong { font-weight: 700; color: var(--ink); }
  article code { font-family: var(--font-mono); font-size: 88%;
    background: var(--accent-wash); color: var(--accent-text);
    padding: 1px 5px; border-radius: 4px; }
  article pre { background: var(--code-bg); color: var(--code-ink);
    font-family: var(--font-mono); font-size: 14px; padding: 14px 16px;
    border-radius: 10px; overflow-x: auto; }
  article pre code { background: none; color: inherit; padding: 0; }
  article blockquote { margin: 0 0 14px; padding: 2px 0 2px 16px;
    border-left: 2px solid var(--accent); color: var(--muted); }
  article hr { border: none; border-top: 1px solid var(--rule); margin: 32px 0; }
  article img.pb-img { display: block; max-width: 100%; max-height: 120px; width: auto; height: auto;
    margin: 8px 0 16px; border: 1px solid var(--rule); border-radius: 6px; }
  :focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .empty { color: var(--muted); }
  @media (max-width: 40rem) { .wrap { padding: 32px 20px 64px; } }
</style>
</head>
<body>
  <div class="wrap">
    <h1 class="title">${safeTitle}</h1>
    <p class="meta">Playbook</p>
    <article>${body || '<p class="empty">This playbook is empty.</p>'}</article>
  </div>
</body>
</html>`;
}
